<template>
  <Page subpage>
    <div class="flex justify-between items-center mb-4">
      <h3 class="page-header-2">Pricing/Finance</h3>
      <SlotWrapper>
        <template #header>
          <Button :disabled="saving" @click="handleSave">
            <span v-if="!saving">Save</span>
            <Spinner v-else message="Saving..." />
          </Button>
        </template>
      </SlotWrapper>
    </div>
    <div v-if="loading">Loading...</div>
    <div v-else>
      <div>
        <div class="d-flex mb-3">
          <Select
            v-model="formData.defaultTaxability"
            data-cy="default-taxability-dropdown"
            :options="taxabilityOptions"
            class="full-width"
            :allow-empty="false"
          >
            <template #label>
              <span class="mr-1">Default Taxability</span>
              <Tooltip placement="top">
                <template #trigger>
                  <div class="d-flex align-items-center">
                    <i class="fas fa-question-circle"></i>
                  </div>
                </template>
                <template #tooltip>
                  <div class="text-center">
                    If set to Taxable, all customers and addresses will be
                    assumed as taxable unless they have tax settings applied.
                  </div>
                </template>
              </Tooltip>
            </template>
          </Select>
        </div>
        <div class="mb-3">
          <Select
            v-if="optionsTaxZones.length"
            v-model="formData.defaultTaxZoneId"
            data-cy="tax-zones-dropdown"
            label="Default Tax Zone (New Customers)"
            placeholder="None"
            :options="optionsTaxZones"
            class="full-width"
          >
            <template #label>
              <DefaultTaxZoneLabel />
            </template>
          </Select>
          <div v-else class="full-width">
            <DefaultTaxZoneLabel class="select-field-label" />
            <div>
              <Alert type="info" class="tax-zone-warning">
                <span>
                  No tax zones available,
                  <a href="/tax_zones" data-cy="setup-tax-zones-link">
                    click here
                  </a>
                  to configure.
                </span>
              </Alert>
            </div>
          </div>
        </div>
        <div class="d-flex mb-2">
          <TextField
            v-model="formData.markup"
            type="number"
            placeholder="Finance Markup %"
            name="markup"
            label="Finance Markup"
            :validation-errors="v$.$errors"
            class="full-width"
          />
        </div>
        <Select
          v-model="formData.defaultQuoteStyleId"
          name="Quote Style"
          placeholder="Select Quote Style"
          class="mb-3 full-width"
          :options="optionsQuoteStyle"
          :field-error="firstErrorMessage(v$.formData.defaultQuoteStyleId)"
          :allow-empty="false"
          label="Quote Style"
        />
        <Select
          v-if="formData.defaultQuoteStyleId !== '0'"
          v-model="formData.defaultPricingPlanId"
          name="Pricing Plan"
          placeholder="Select Membership Program"
          class="mb-3 full-width"
          :options="optionsPricingPlan"
          label="Default Membership Program"
        />
        <div class="mb-3">
          <div class="pricing-toggle-setting">
            <div class="label">
              Task Pricing Adjustment in the Technician App
            </div>
            <ToggleSwitch v-model="formData.pricingAdjustmentsEnabled" />
          </div>
          <p class="mb-2">
            Allow technicians to override the given task price from the
            pricebook on quotes & invoices within the Technician App.
          </p>
          <a
            :href="pricingAdjustmentsLearnMoreLink"
            target="_blank"
            class="link"
            >Learn More</a
          >
        </div>
        <div
          v-if="showMembershipSavingsToggles"
          class="mb-3"
          data-cy="membership-savings-toggle"
        >
          <div class="flex justify-between items-center">
            <div class="label">
              <span class="mr-1"
                >Display Membership Savings on Quotes & Invoices</span
              >
              <Tooltip placement="top">
                <template #trigger>
                  <div class="d-flex align-items-center">
                    <i class="fas fa-question-circle"></i>
                  </div>
                </template>
                <template #tooltip>
                  <div class="text-center">
                    Set the default visibility for membership savings across all
                    quotes and invoices.
                  </div>
                </template>
              </Tooltip>
            </div>
            <ToggleSwitch v-model="formData.displayMembershipSavings" />
          </div>
        </div>
        <div
          v-if="showMembershipSavingsToggles"
          class="mb-3"
          data-cy="membership-savings-ad-hoc-toggle"
        >
          <div class="flex justify-between items-center">
            <div class="label">
              <span class="mr-1">
                Allow Ad-Hoc Control of Membership Savings
              </span>
              <Tooltip placement="top">
                <template #trigger>
                  <div class="d-flex align-items-center">
                    <i class="fas fa-question-circle"></i>
                  </div>
                </template>
                <template #tooltip>
                  <div class="text-center">
                    Enable users to customize membership savings visibility for
                    individual quotes and invoices
                  </div>
                </template>
              </Tooltip>
            </div>
            <ToggleSwitch v-model="formData.displayMembershipSavingsAdHoc" />
          </div>
        </div>
      </div>
    </div>
  </Page>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { getProperty } from "@/lib/helpers";
import { firstErrorMessage } from "@/lib/vuelidate_helpers";

import Button from "@/components/Button.vue";
import Page from "@/components/Page.vue";
import Alert from "@/components/Seranova/Alert/Alert.vue";
import SlotWrapper from "@/components/SlotWrapper";
import ToggleSwitch from "@/components/ToggleSwitch.vue";
import { getTaxabilityOptions } from "@/typings/taxability";
import useVuelidate from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import Spinner from "../components/Spinner.vue";
import TextField from "../components/TextField.vue";
import DefaultTaxZoneLabel from "./PricingSettings/DefaultTaxZoneLabel.vue";

export default {
  components: {
    SlotWrapper,
    Spinner,
    TextField,
    Page,
    DefaultTaxZoneLabel,
    ToggleSwitch,
    Button,
    Alert
  },
  props: {
    defaultTaxability: { type: String, default: "nontaxable" },
    defaultTaxZoneId: { type: Number, default: null },
    tenantId: { type: Number, default: null },
    salesTaxRate: { type: String, default: "" },
    financeMarkup: { type: String, default: "" },
    selectedPricingPlanId: { type: Number, default: null },
    selectedQuoteStyleId: { type: String, default: "" },
    quotes: { type: Array, default: () => [] },
    pricingAdjustmentsEnabled: { type: Boolean, default: false },
    displayMembershipSavings: { type: Boolean, default: true },
    displayMembershipSavingsAdHoc: { type: Boolean, default: false },
    onSave: { type: Function, default: null }
  },
  data() {
    return {
      taxabilityOptions: getTaxabilityOptions("Company"),
      loading: true,
      saving: false,
      formData: {
        tax: null,
        markup: null,
        defaultTaxZoneId: null,
        defaultTaxability: "nontaxable",
        defaultPricingPlanId: null,
        defaultQuoteStyleId: null,
        pricingAdjustmentsEnabled: false,
        displayMembershipSavings: true,
        displayMembershipSavingsAdHoc: false
      }
    };
  },
  computed: {
    ...mapState(["membershipPrograms", "taxability"]),
    ...mapGetters(["featureEnabledForTenant"]),
    formDataJSON() {
      return JSON.stringify(this.formData);
    },
    optionsTaxZones() {
      return this.taxability.companyTaxZones.map((taxZone) => {
        return {
          label: taxZone.formatted_name,
          value: taxZone.id
        };
      });
    },
    optionsPricingPlan() {
      if (!this.membershipPrograms) {
        return [];
      }
      return this.membershipPrograms
        .filter((mp) => mp.is_active)
        .map((mp) => ({
          value: mp.id,
          label: mp.name
        }));
    },
    optionsQuoteStyle() {
      if (!this.quotes) {
        return [];
      }
      return this.quotes.map((qs) => ({
        value: qs.id,
        label: qs.name
      }));
    },
    quoteStyle() {
      const quote = this.quotes.find(
        (qs) => qs.id === this.formData.defaultQuoteStyleId
      );
      if (!quote) {
        return "Not Available";
      }
      return quote.name;
    },
    membershipName() {
      const mp = this.membershipPrograms.find(
        (mp) => mp.id === this.formData.defaultPricingPlanId
      );
      if (!mp) {
        return "Not Available";
      }
      return mp.name;
    },
    showMembershipSavingsToggles() {
      return this.featureEnabledForTenant("toggle_display_membership_savings");
    },
    pricingAdjustmentsLearnMoreLink: function () {
      const link = getProperty(
        this.$store.state.tenantCustomizations,
        "System.pricing_adjustments_learn_more_link"
      );
      return link?.value || link?.default;
    }
  },
  watch: {
    "formData.defaultQuoteStyleId"(newValue, oldValue) {
      if (newValue !== oldValue && newValue === "0") {
        this.formData.defaultPricingPlanId = null;
      }
    }
  },
  async mounted() {
    try {
      if (this.membershipPrograms.length === 0) {
        await this.fetchMembershipPrograms();
      }
      if (!this.taxability.companyTaxZones.length) {
        await this.fetchCompanyTaxZones();
      }
    } catch (e) {
      // Do nothing for now
    }
    this.loading = false;
    this.formData.defaultTaxZoneId = this.defaultTaxZoneId;
    this.formData.defaultTaxability = this.defaultTaxability;
    this.formData.defaultQuoteStyleId = this.selectedQuoteStyleId;
    this.formData.defaultPricingPlanId = this.selectedPricingPlanId;
    this.formData.tax = this.salesTaxRate;
    this.formData.markup = this.financeMarkup;
    this.formData.pricingAdjustmentsEnabled = this.pricingAdjustmentsEnabled;
    this.formData.displayMembershipSavings = this.displayMembershipSavings;
    this.formData.displayMembershipSavingsAdHoc =
      this.displayMembershipSavingsAdHoc;
  },
  setup() {
    return {
      v$: useVuelidate()
    };
  },
  validations() {
    return {
      formData: {
        defaultQuoteStyleId: { required, $autoDirty: true },
        markup: {
          required,
          validateNumberFormat: helpers.withMessage(
            "Finance Markup must be a valid number`",
            function (value) {
              if (value === "" || value === null || value === undefined) {
                return true;
              }
              const regex = /^[+-]?([0-9]+(\.[0-9]+)?)$/;
              return regex.test(value);
            }
          ),
          validateMaxTwoDecimals: helpers.withMessage(
            "Finance Markup must have no more than two decimal places",
            function (value) {
              if (value === "" || value === null || value === undefined) {
                return true;
              }
              const regex = /^[+-]?([0-9]+)?(\.\d{0,2})?$/;
              return regex.test(value);
            }
          ),
          $autoDirty: true
        }
      }
    };
  },
  methods: {
    ...mapActions(["fetchMembershipPrograms", "fetchCompanyTaxZones"]),
    firstErrorMessage,
    handleSave() {
      this.v$.$validate().then((valid) => {
        if (valid) {
          this.saving = true;
          const payload = {
            sales_tax_rate: this.formData.tax,
            finance_markup: this.formData.markup,
            default_quote_type_id: this.formData.defaultQuoteStyleId,
            default_membership_program_id: this.formData.defaultPricingPlanId,
            default_tax_zone_id: this.formData.defaultTaxZoneId,
            default_taxability: this.formData.defaultTaxability,
            pricing_adjustments_enabled:
              this.formData.pricingAdjustmentsEnabled,
            display_membership_savings: this.formData.displayMembershipSavings,
            display_membership_savings_ad_hoc:
              this.formData.displayMembershipSavingsAdHoc
          };
          $.ajax(`/company/settings`, {
            method: "PUT",
            contentType: "application/json",
            dataType: "json",
            data: JSON.stringify({
              settings: payload
            }),
            success: () => {
              this.saving = false;
              if (this.onSave) {
                this.onSave(payload);
              }
              window.notify.success("Settings updated successfully!");
            },
            error: (e) => {
              this.saving = false;
              window.notify.error(
                (e.responseJSON || {}).message || "Unable to save settings"
              );
            }
          });
        }
      });
    }
  }
};
</script>

<style scoped>
.tax-zone-warning {
  margin-top: 4px;
}
.pricing-toggle-setting {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  align-items: center;
}
</style>
